import React, { useCallback, useEffect, useState } from "react";
import {
  getRenewalTimeline,
  type RenewalTimelinePreview,
} from "../services/accountService";
import { formatCurrency, formatDate, getBillingIntervalLabel } from "../utils/billingFormat";

export interface SubscriptionTimelineProps {
  /** Same status gate as the section this component replaces (AC-14, H-15). */
  status: "active" | "trialing";
  currentProductName: string;
  /** Start of the current period — the first dot of the axis. */
  currentPeriodStart: string | null;
  /** End of the current period (Y). */
  currentPeriodEnd: string | null;
  autoRenewal: boolean;
  onEnableAutoRenewal: () => void;
  autoRenewalPending?: boolean;
  onOpenPackageModal: () => void;
}

/** Warning icon for the "preference unavailable" state (#8). */
const WarningIcon: React.FC = () => (
  <svg
    data-testid="subscription-timeline-warning-icon"
    className="h-4 w-4 mt-0.5 text-blue-700 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

function priceText(price: RenewalTimelinePreview["price"]): string {
  if (!price) return "cena chwilowo niedostępna";
  return `${formatCurrency(price.netCents, price.currency)} netto`;
}

/** A point on the axis — a dot with its date next to it. */
interface Moment {
  /** ISO timestamp for `<time datetime>`. */
  at: string | null;
  label: string;
  testId?: string;
}

function dateMoment(at: string | null, testId: string): Moment {
  return { at, label: formatDate(at), testId };
}

/**
 * D-3: fixed semantic palette (not `primary-*`) — green = active, blue = scheduled/paid,
 * red = none. The line style carries the meaning too: solid = running/paid, dashed = to pay/none.
 */
type SegmentTone = "active" | "pending" | "paid" | "none";

const TONE_CLASSES: Record<SegmentTone, { dot: string; line: string }> = {
  active: { dot: "bg-green-600", line: "border-green-600" },
  pending: { dot: "bg-blue-500", line: "border-dashed border-blue-400" },
  paid: { dot: "bg-blue-500", line: "border-blue-500" },
  none: { dot: "bg-red-500", line: "border-dashed border-red-400" },
};

/** Rail piece above/below a dot — keeps the line continuous through the dot. */
function railPiece(tone: SegmentTone | null): string {
  return `w-0 border-l-2 ${tone ? TONE_CLASSES[tone].line : "border-transparent"}`;
}

interface AxisMomentProps {
  moment: Moment;
  tone: SegmentTone;
  /** Line arriving at the dot from above (null = the axis starts here). */
  above: SegmentTone | null;
  /** Line leaving the dot downwards (null = the axis ends here). */
  below: SegmentTone | null;
  /** Hollow dot — „dziś”, a point inside a period rather than its boundary. */
  hollow?: boolean;
}

/** A dot on the vertical rail with its date to the right. */
const AxisMoment: React.FC<AxisMomentProps> = ({ moment, tone, above, below, hollow = false }) => (
  <div className="flex gap-3">
    <div aria-hidden="true" className="flex flex-col items-center w-3 flex-shrink-0">
      <span className={`h-1.5 ${railPiece(above)}`} />
      <span
        className={`h-3 w-3 rounded-full flex-shrink-0 ${
          hollow ? "bg-white border-2 border-green-600" : TONE_CLASSES[tone].dot
        }`}
      />
      <span className={`flex-1 ${railPiece(below)}`} />
    </div>
    <span data-testid={moment.testId} className="text-sm font-medium text-gray-700 py-0.5">
      <time dateTime={moment.at ?? undefined}>{moment.label}</time>
    </span>
  </div>
);

/** A stretch of the rail with the period's description next to it. */
const AxisSpan: React.FC<{ tone: SegmentTone; children?: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <div className="flex gap-3">
    <div aria-hidden="true" className="flex justify-center w-3 flex-shrink-0">
      <span data-testid="subscription-timeline-line" className={railPiece(tone)} />
    </div>
    <div className="min-w-0 py-2">{children}</div>
  </div>
);

const SegmentTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{children}</p>
);

/**
 * Oś czasu subskrypcji (AC-14), pionowa na każdej szerokości: kropki na granicach okresów
 * (początek bieżącego → koniec bieżącego → koniec kolejnego) i pusta kropka „dziś” wewnątrz
 * bieżącego okresu; opis okresów obok linii. Renderowana w karcie „Twoja subskrypcja”
 * zamiast pól z datami i dawnej sekcji „Następna płatność" — pod tymi samymi guardami
 * (`research/timeline-states.md`); w odróżnieniu od niej NIE znika przy wystawionej proformie.
 */
export const SubscriptionTimeline: React.FC<SubscriptionTimelineProps> = ({
  status,
  currentProductName,
  currentPeriodStart,
  currentPeriodEnd,
  autoRenewal,
  onEnableAutoRenewal,
  autoRenewalPending = false,
  onOpenPackageModal,
}) => {
  const [preview, setPreview] = useState<RenewalTimelinePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // State #3 (auto-renewal off) and #11 (trialing) never need the preview — no fetch, no
  // loading/error flash for cases the API call can't change the outcome of.
  const shouldFetch = status === "active" && autoRenewal;

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getRenewalTimeline()
      .then((data) => {
        setPreview(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (shouldFetch) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, reloadToken]);

  const todayIso = new Date().toISOString();
  const today: Moment = {
    at: todayIso,
    label: `dziś, ${formatDate(todayIso)}`,
    testId: "subscription-timeline-marker-today",
  };
  const periodEnd = dateMoment(currentPeriodEnd, "subscription-timeline-marker-y");

  // The current period closes the axis itself only when no next period is drawn after it.
  const currentSegment = (closesAxis: boolean) => (
    <li data-testid="subscription-timeline-segment-current">
      {currentPeriodStart && (
        <AxisMoment
          moment={dateMoment(currentPeriodStart, "subscription-timeline-marker-start")}
          tone="active"
          above={null}
          below="active"
        />
      )}
      <AxisSpan tone="active">
        <SegmentTitle>Bieżący okres</SegmentTitle>
        <p className="font-semibold text-gray-900 break-words">{currentProductName}</p>
        <p className="text-sm text-green-700">
          {status === "trialing" ? "okres próbny" : "aktywne"}
        </p>
      </AxisSpan>
      <AxisMoment moment={today} tone="active" above="active" below="active" hollow />
      <AxisSpan tone="active" />
      {closesAxis && <AxisMoment moment={periodEnd} tone="active" above="active" below={null} />}
    </li>
  );

  const section = (content: React.ReactNode) => (
    <section aria-label="Oś czasu subskrypcji" data-testid="subscription-timeline">
      {content}
    </section>
  );

  if (status === "trialing") {
    return section(<ol>{currentSegment(true)}</ol>);
  }

  if (!autoRenewal) {
    return section(
      <ol>
        {currentSegment(false)}
        <li data-testid="subscription-timeline-segment-next" data-status="none">
          <AxisMoment moment={periodEnd} tone="none" above="active" below="none" />
          <AxisSpan tone="none">
            <SegmentTitle>Kolejny okres</SegmentTitle>
            <p className="text-sm text-red-700">brak subskrypcji</p>
            <p className="text-gray-800">Brak subskrypcji po {formatDate(currentPeriodEnd)}</p>
            <button
              type="button"
              onClick={onEnableAutoRenewal}
              disabled={autoRenewalPending}
              className="mt-2 px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-50"
            >
              {autoRenewalPending ? "Zapisywanie..." : "Włącz przedłużenie"}
            </button>
          </AxisSpan>
        </li>
      </ol>
    );
  }

  return section(
    <>
      {loading && (
        <div
          data-testid="subscription-timeline-loading"
          aria-live="polite"
          aria-busy="true"
          className="animate-pulse flex flex-col gap-3"
        >
          <div className="h-16 bg-gray-100 rounded" />
          <div className="h-16 bg-gray-100 rounded" />
        </div>
      )}

      {/* Stan #2 — błąd preview: bieżący okres jest znany z propsów, więc zostaje;
          zamiast kolejnego okresu komunikat + ponowienie (research/timeline-states.md, review H-10). */}
      {!loading && error && (
        <ol>
          {currentSegment(true)}
          <li data-testid="subscription-timeline-error" className="pl-6 pt-2">
            <div role="alert">
              <p className="text-red-700 text-sm mb-2">
                Nie udało się wczytać planu na kolejny okres
              </p>
              <button
                type="button"
                onClick={() => setReloadToken((t) => t + 1)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Spróbuj ponownie
              </button>
            </div>
          </li>
        </ol>
      )}

      {!loading && !error && preview && (
        <ol>
          {currentSegment(false)}
          <NextSegment
            preview={preview}
            start={periodEnd}
            onOpenPackageModal={onOpenPackageModal}
          />
        </ol>
      )}
    </>
  );
};

interface NextSegmentProps {
  preview: RenewalTimelinePreview;
  /** End of the current period — where the next one starts. */
  start: Moment;
  onOpenPackageModal: () => void;
}

interface NextPeriodProps {
  dataStatus: "pending" | "paid";
  tone: SegmentTone;
  start: Moment;
  end: Moment | null;
  children: React.ReactNode;
}

/** Next period: dot at its start, the description, and a closing dot at its end (Z). */
const NextPeriod: React.FC<NextPeriodProps> = ({ dataStatus, tone, start, end, children }) => (
  <li data-testid="subscription-timeline-segment-next" data-status={dataStatus}>
    <AxisMoment moment={start} tone={tone} above="active" below={tone} />
    <AxisSpan tone={tone}>
      <SegmentTitle>Kolejny okres</SegmentTitle>
      {children}
    </AxisSpan>
    {end && <AxisMoment moment={end} tone={tone} above={tone} below={null} />}
  </li>
);

const NextSegment: React.FC<NextSegmentProps> = ({ preview, start, onOpenPackageModal }) => {
  const { renewalProduct, billingInterval, price, nextPeriodEnd, renewalSource } = preview;
  const intervalLabel = getBillingIntervalLabel(billingInterval);
  // AC-16 (review H-9): with the feature flags off the backend reports `false` and the modal's
  // endpoints return 404 — never offer a dead CTA. Hidden only on an explicit `false`.
  const canChangePackage = preview.renewalPreferenceAvailable !== false;
  const nextEnd = nextPeriodEnd
    ? dateMoment(nextPeriodEnd, "subscription-timeline-marker-z")
    : null;

  const renewalSummary = (
    <>
      <p className="font-semibold text-gray-900 break-words">
        {renewalHeadline(renewalSource)}: {renewalProduct.name}
      </p>
      <p className="text-sm text-gray-700">
        {intervalLabel} · {priceText(price)}
      </p>
    </>
  );

  // Stan #7 — opłacone (SCHEDULED): zmiana zablokowana, brak CTA.
  if (preview.scheduledSuccessor) {
    const s = preview.scheduledSuccessor;
    return (
      <NextPeriod
        dataStatus="paid"
        tone="paid"
        start={start}
        end={dateMoment(s.periodEnd, "subscription-timeline-marker-z")}
      >
        <p className="font-semibold text-gray-900 break-words">{s.productName}</p>
        <p className="text-sm text-gray-700">{getBillingIntervalLabel(s.billingInterval)}</p>
        <p className="text-sm text-blue-700">opłacone</p>
      </NextPeriod>
    );
  }

  // Stan #10 — zmiana zablokowana (MANUAL): brak CTA.
  if (preview.preferenceBlockedReason === "MANUAL_ORDER_OPEN") {
    return (
      <NextPeriod dataStatus="pending" tone="pending" start={start} end={nextEnd}>
        {renewalSummary}
        <p className="text-sm text-blue-700">do opłacenia</p>
        <p className="text-gray-600 text-sm mt-1">Zmianę pakietu obsługuje nasz zespół</p>
      </NextPeriod>
    );
  }

  // Stan #8 — preferencja niedostępna: fallback ostrzeżenie, CTA „Wybierz pakiet".
  // Bez dostępnej funkcji „wybierz inny" byłoby martwą instrukcją — wtedy zwykłe przedłużenie.
  if (renewalSource === "own_fallback" && canChangePackage) {
    return (
      <NextPeriod dataStatus="pending" tone="pending" start={start} end={nextEnd}>
        {renewalSummary}
        <p className="text-sm text-blue-700">do opłacenia</p>
        <p className="mt-1 text-sm text-gray-800 flex items-start gap-1">
          <WarningIcon />
          Ten pakiet nie jest już dostępny — wybierz inny. Bez zmiany odnowimy{" "}
          {renewalProduct.name}.
        </p>
        <ProformaNote preview={preview} />
        <button
          type="button"
          onClick={onOpenPackageModal}
          className="mt-2 px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800"
        >
          Wybierz pakiet
        </button>
      </NextPeriod>
    );
  }

  // Stany #4/#5/#6/#9 — do opłacenia (bez zmian / preferencja / proforma / cena niedostępna).
  return (
    <NextPeriod dataStatus="pending" tone="pending" start={start} end={nextEnd}>
      {renewalSummary}
      <p className="text-sm text-blue-700">do opłacenia</p>
      <ProformaNote preview={preview} />
      {canChangePackage && (
        <button
          type="button"
          onClick={onOpenPackageModal}
          className="mt-2 text-sm text-primary-700 underline hover:text-primary-800"
        >
          Zmień pakiet na kolejny okres
        </button>
      )}
    </NextPeriod>
  );
};

/** Issued renewal proforma (state #6), otherwise when the CRON will send it (AC-E3). */
const ProformaNote: React.FC<{ preview: RenewalTimelinePreview }> = ({ preview }) => {
  if (preview.renewalProforma) {
    return (
      <p className="text-gray-600 text-sm mt-1">
        Proforma {preview.renewalProforma.number}, termin płatności{" "}
        <time dateTime={preview.renewalProforma.dueDate ?? undefined}>
          {formatDate(preview.renewalProforma.dueDate)}
        </time>
      </p>
    );
  }
  if (preview.proformaExpectedAt) {
    return (
      <p className="text-gray-600 text-sm mt-1">
        Proforma zostanie wysłana ok.{" "}
        <time dateTime={preview.proformaExpectedAt}>
          {formatDate(preview.proformaExpectedAt)}
        </time>
      </p>
    );
  }
  return null;
};

function renewalHeadline(source: RenewalTimelinePreview["renewalSource"]): string {
  return source === "preference" ? "Zmiana na" : "Przedłużenie";
}

export default SubscriptionTimeline;
